import { useState, useRef, useEffect } from 'react';

export default function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ shiftLeft: false, shiftRight: false });
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const isTouchDevice = useRef(
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );

  useEffect(() => {
    if (!visible) return;

    // Calculate tooltip overflow on both edges
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const tooltipW = 260;
      const overflowRight = rect.left + tooltipW / 2 > window.innerWidth - 12;
      const overflowLeft  = rect.left - tooltipW / 2 < 12;
      setPosition({ shiftLeft: overflowRight, shiftRight: overflowLeft });
    }

    // Close on outside click/tap
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setVisible(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [visible]);

  // Tooltip position transform
  const transform = position.shiftLeft
    ? 'translateX(-85%)'
    : position.shiftRight
    ? 'translateX(-15%)'
    : 'translateX(-50%)';

  const handleMouseEnter = () => {
    if (isTouchDevice.current) return;
    setHovered(true);
    setVisible(true);
  };

  const handleMouseLeave = () => {
    if (isTouchDevice.current) return;
    setHovered(false);
    setVisible(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setVisible(v => !v);
  };

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
      <button
        aria-label="Több információ"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        style={{
          width: 18, height: 18, borderRadius: '50%',
          border: `1.5px solid ${hovered ? 'rgba(var(--accent-rgb),0.9)' : 'rgba(var(--accent-rgb),0.5)'}`,
          background: hovered ? 'rgba(var(--accent-rgb),0.2)' : 'rgba(var(--accent-rgb),0.08)',
          cursor: 'pointer',
          fontSize: 11, fontWeight: 700, lineHeight: 1,
          color: hovered ? 'rgba(var(--accent-rgb),1)' : 'rgba(var(--accent-rgb),0.7)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
          transition: 'all 0.15s', flexShrink: 0,
        }}
      >
        ?
      </button>
      {visible && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%',
            transform, width: 260,
            background: '#1a1a2e', border: '1px solid rgba(0,210,200,0.3)',
            borderRadius: 8, padding: '10px 14px', fontSize: 13,
            color: 'rgba(255,255,255,0.85)', lineHeight: 1.6,
            zIndex: 9999, pointerEvents: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
